import { Checkbox, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ButtonStyled } from './AssigneeFilter.styled';
import { useTickets } from 'contexts/TicketsContext';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';

const assignToOptions = ['Dealer', 'WarrCloud', 'OEM'];

export const AssignToFilterDropdown = (): JSX.Element => {
  /**
   * Hooks
   */
  const { getSearchParam, updateSearchParamsArray } = useTickets();
  const [searchParams] = useSearchParams();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [checked, setChecked] = useState<string[]>((): string[] => {
    const selected = getSearchParam(TicketsRefinementKeysE.Assignee)?.split(',') || [];
    return selected;
  });

  /**
   * Handlers
   */
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleToggle = (name: string) => (): void => {
    const currentIndex = checked.indexOf(name);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(name);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
    setIsAllSelected(newChecked.length === assignToOptions.length);
    updateSearchParamsArray(TicketsRefinementKeysE.Assignee, newChecked);
  };

  const handleSelectAll = (): void => {
    if (isAllSelected) {
      setChecked([]);
      setIsAllSelected(false);
      updateSearchParamsArray(TicketsRefinementKeysE.Assignee, []);
    } else {
      setChecked(assignToOptions);
      setIsAllSelected(true);
      updateSearchParamsArray(TicketsRefinementKeysE.Assignee, ['All']);
    }
  };

  /**
   * Effects
   */
  useEffect(() => {
    const selected = getSearchParam(TicketsRefinementKeysE.Assignee)?.split(',') || [];
    if (selected.includes('All')) {
      setChecked(assignToOptions);
      setIsAllSelected(true);
    } else {
      setChecked(selected);
      setIsAllSelected(selected.length === assignToOptions.length);
    }
  }, [searchParams, getSearchParam]);

  return (
    <div>
      <Tooltip
        title={isAllSelected ? 'All' : checked.join(', ') || 'Assign To Filter'}
        placement="top"
      >
        <ButtonStyled
          aria-controls="assign-to-menu"
          aria-haspopup="true"
          variant="outlined"
          onClick={handleClick}
          style={{
            maxWidth: '140px',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            justifyContent: 'start',
          }}
        >
          {isAllSelected ? 'All' : checked.join(', ') || 'Assign To Filter'}
        </ButtonStyled>
      </Tooltip>
      <Menu
        id="assign-to-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={handleSelectAll}>
          <ListItemIcon>
            <Checkbox
              edge="start"
              checked={isAllSelected}
              indeterminate={checked.length > 0 && checked.length < assignToOptions.length}
              tabIndex={-1}
              disableRipple
            />
          </ListItemIcon>
          <ListItemText primary="Select All" />
        </MenuItem>
        {assignToOptions.map(
          (name): JSX.Element => (
            <MenuItem key={name} onClick={handleToggle(name)}>
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={checked.includes(name)}
                  tabIndex={-1}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemText primary={name} />
            </MenuItem>
          )
        )}
      </Menu>
    </div>
  );
};

export default AssignToFilterDropdown;
